/**
 * Created by Ning Jiang on 2/7/2018.
 */

import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageEvent} from "@nolimitcity/slot-game/bin/core/stage/event/StageEvent";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {BitmapTextStyleOptions} from "@nolimitcity/slot-game/bin/core/text/BitmapTextStyleOptions";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {StepCountUpBigWinController} from "@nolimitcity/slot-game/bin/game/winpresentation/bigwin/stepcountup/StepCountUpBigWinController";
import {Timeline, TimelineLite} from "gsap";
import {EdisonHelper} from "../../../utils/EdisonHelper";
import {EdisonGameConfig} from "../../EdisonGameConfig";
import {EdisonGameModuleConfig} from "../../EdisonGameModuleConfig";
import {EdisonSoundConfig} from "../../EdisonSoundConfig";
import {EdisonGameMode} from "../../gameMode/EdisonGameMode";
import {EdisonIBetWayWinData, EdisonParsedGameData} from "../../server/data/EdisonParsedGameData";
import {EdisonBigWinCountUp} from "./EdisonBigWinCountUp";
import {EdisonBigWinView} from "./EdisonBigWinView";

export class EdisonBigWinController extends StepCountUpBigWinController {

    private _gameView:EdisonBigWinView;
    private _winCountUp:EdisonBigWinCountUp;
    private _reelsNum:number;
    private _symbolsNum:number;
    private _aspectRatio:number;
    private _aborted:boolean = false;

    private _style:BitmapTextStyleOptions = {
        font : {
            name : "NumbersWinCountUp",
            size : 180
        },
        tint : parseInt(("#ffffff").replace(/^#/, ''), 16)
    };

    private _symbolWinStates:boolean[][];

    constructor(indexes:[number, number]) {
        super(indexes, {skipLastLevelCountUpIfSmall : true});

        this._reelsNum = GameConfig.instance.REELS_NUM;
        this._symbolsNum = GameConfig.instance.SYMBOLS_NUM_IN_REEL[2];

        this._gameView = new EdisonBigWinView();
        this._winCountUp = new EdisonBigWinCountUp(this._style);
        StageManager.getLayer(EdisonGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._winCountUp);
        EventHandler.addFirstEventListener(this, StageEvent.STAGE_RESIZED, (event:GameEvent) => this.onResize(event.params[0]));
    }

    protected parseFeatureGameData(data:EdisonParsedGameData):void {
        super.parseFeatureGameData(data);

        this._symbolWinStates = ArrayHelper.initArrayWithValues<boolean[]>(this._reelsNum, () => {
            return ArrayHelper.initArrayWithValues<boolean>(this._symbolsNum, () => false)
        });

        if(data.betWayWins!.length > 0)
            this.parseSymbolWinStates(data.betWayWins!);
    }

    private parseSymbolWinStates(betWinsArr:EdisonIBetWayWinData[]) {
        betWinsArr.forEach((object) => {
            for(let i:number = 0; i < EdisonGameConfig.instance.REELS_NUM; i++) {
                for(let j:number = 0; j < EdisonGameConfig.instance.SYMBOLS_NUM_IN_REEL[2]; j++) {
                    if(object.reelWinPositions[i][j])
                        this._symbolWinStates[i][j] = true;
                }
            }
        });
    }

    protected onResize(resizeData:IResizeData):void {
        const layer:ScalableLayer = StageManager.getLayer(EdisonGameConfig.instance.LAYERS.WIN_COUNT_UP.name);
        const screenWidth = resizeData.width / layer.scale.x;
        const screenHeight = resizeData.height / layer.scale.y;
        this._aspectRatio = screenWidth / screenHeight;

        const extraYGap:number = this._aspectRatio > 3 / 4 ? 70 : 0;


        this._winCountUp.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.5 + extraYGap);
    }

    protected startWinPresentation():void {

        let tl:TimelineLite = new TimelineLite();
        this.playNoWinAnimations(tl, 0);

        EdisonGameModuleConfig.background.dimBackground(true);
        this.playWinAnimations(tl);

        tl.add(() => {
            if(!this._aborted)
                super.startWinPresentation();
        }, 2);
    }

    private playNoWinAnimations(timeline:Timeline, duration:number):void {
        this._aborted = false;
        const noWinDelay:number = .4;


        if(this._gameData!.mode !== EdisonGameMode.FREESPIN)
            timeline.add(() => {
                EdisonGameModuleConfig.background.changeReelBackground("winpresentation");
            }, noWinDelay);
        else
            timeline.add(() => {
                EdisonGameModuleConfig.background.changeReelBackground("freespinWP");
            }, noWinDelay);

        timeline.add(() => EdisonGameModuleConfig.scatterOverlay.setNoWin(), noWinDelay);

        SlotGame.sound.playEffect(EdisonSoundConfig.instance.WIN_EFFECT_LARGE);
        SlotGame.sound.fadeAmbience(0, 300);

        const normalNoWinSymbols:(StateReelSymbol | null)[] = [];
        const goldNoWinSymbols:(StateReelSymbol | null)[] = [];

        this._symbolWinStates.forEach((reel, reelIndex) => {
            reel.forEach((sym, symIndex) => {
                if(!sym) {
                    if(this.checkGoldSym(reelIndex, symIndex)) {
                        goldNoWinSymbols.push(Reels.getSymbol(reelIndex, symIndex))
                    } else {
                        normalNoWinSymbols.push(Reels.getSymbol(reelIndex, symIndex))
                    }
                }
            });
        });


        normalNoWinSymbols.forEach((noWinSymbol) => {
            timeline.add(() => {
                noWinSymbol!.changeState({
                    state : EdisonGameConfig.instance.SYMBOL_STATES.noWin!,
                    fadeInDuration : duration
                })
            }, noWinDelay);
        });

        goldNoWinSymbols.forEach((noWinSymbol) => {
            timeline.add(() => {
                noWinSymbol!.changeState({
                    state : EdisonGameConfig.instance.SYMBOL_STATES.noWinFS!,
                    fadeInDuration : duration
                })
            }, noWinDelay);
        });

        (<EdisonParsedGameData>this._gameData).stackedModeOverlay.forEach(
            (overlay, index) => {
                if(overlay) {
                    if(!this.checkStackedWin(index))
                        timeline.add(() => {
                            EdisonGameModuleConfig.respinOverlay.tintOverlay(index);
                        }, noWinDelay);
                }
            }
        );
    }

    private checkGoldSym(xIndex:number, yIndex:number):boolean {
        return (<EdisonParsedGameData>this._gameData)!.evaluatedArea[xIndex][yIndex].indexOf("W") !== -1;
    }

    protected playWinAnimations(timeline:Timeline):void {
        for(let i:number = 0; i < this._symbolWinStates.length; i++) {
            for(let j:number = 0; j < this._symbolWinStates[i].length; j++) {
                if(this._symbolWinStates[i][j]) {
                    let stacked:boolean = EdisonHelper.checkStacked(<EdisonParsedGameData>this._gameData, i);
                    timeline.add(() => {
                        if(!this._aborted)
                            EdisonGameModuleConfig.winPresentationView.playFrameOverlay(i, stacked ? 1 : j, true, stacked);
                    });
                }
            }
        }

        for(let i:number = 0; i < this._symbolWinStates.length; i++) {
            for(let j:number = 0; j < this._symbolWinStates[i].length; j++) {
                if(this._symbolWinStates[i][j]) {
                    let stacked:boolean = EdisonHelper.checkStacked(<EdisonParsedGameData>this._gameData, i);
                    timeline.add(() => {
                        if(!this._aborted)
                            EdisonGameModuleConfig.winPresentationView.setFrameImage(i * 3 + j, stacked);
                    }, 1.6);
                }
            }
        }
    }

    private checkStackedWin(reelIndex:number):boolean {
        let returnValue:boolean = false;
        (<EdisonParsedGameData>this._gameData).betWayWins!.forEach((win) => {
            let count:number = win.reelWinPositions[reelIndex].filter(function(elm) {return elm}).length;
            if((win.symbol === "M1" || win.symbol === "M2") && count === 3)
                returnValue = true;
        });
        return returnValue;
    }

    protected getBigWinLevelAnimation(level:number):Timeline {
        EventHandler.dispatchEvent(new GameEvent(EdisonBigWinCountUp.SET_RATIO, level));
        return this._gameView.getBigWinLevelAnimation(this._winRatios[level]);
    }

    protected playBigWinCountUp(level:number, duration:number):Timeline | null {
        const preTotalWin:number = this._gameData!.totalWin - this._gameData!.singleWin;
        const fromValue:number = level === 0 ? 0 : this._gameData!.playedBetValue * GameConfig.instance.WIN_RATIOS.bigWin[level].ratio;
        const toValue:number = level === this._winLevel ? this._gameData!.singleWin : this._gameData!.playedBetValue * GameConfig.instance.WIN_RATIOS.bigWin[level + 1].ratio;

        SlotGame.sound.playEffect((<any>EdisonSoundConfig.instance)[`BIG_WIN_LEVEL_${level + 1}`]);
        const timeline:TimelineLite = new TimelineLite();
        timeline.add([
            this.playCountUp(fromValue, toValue, EdisonGameConfig.instance.WIN_RATIOS.bigWin[level].duration, preTotalWin),
            () => {
                if(this.playBigWinCountUpSound) {
                    this.playBigWinCountUpSound(level);
                }
            }
        ]);

        return timeline;
    }

    protected playBigWinCountUpSound(level:number):void {
        SlotGame.sound.playEffect(EdisonSoundConfig.instance.COUNT_UP, -1);
    }

    protected stopAllSounds():void {
        SlotGame.sound.playEffect(EdisonSoundConfig.instance.BIG_WIN_END);
        SlotGame.sound.stopEffect(EdisonSoundConfig.instance.COUNT_UP);
        for(let i:number = 1; i < 4; i++)
            SlotGame.sound.stopEffect((<any>EdisonSoundConfig.instance)[`BIG_WIN_LEVEL_${i}`]);

        SlotGame.sound.fadeAmbience(1, 1000);
    }

    protected stopWinPresentation(isAborted:boolean):void {
        super.stopWinPresentation(isAborted);

        if((<EdisonParsedGameData>this._gameData)!.mode === EdisonGameMode.STACKED_RESPIN) {
            EdisonGameModuleConfig.background.currentMode = "normal";
        }

        this.stopAllSounds();
    }

    protected playCountUp(from:number, to:number, duration:number, preTotalWin:number = 0):Timeline | null {
        return this._winCountUp.getCountUpAnimation(from, to, duration);
    }

    protected playEndingAnimation(isAborted:boolean):void {
        EventHandler.dispatchEvent(new GameEvent(EdisonBigWinCountUp.SET_RATIO, this._winLevel));
        this._gameView.playEndingAnimation(this._winRatios[this._winLevel], isAborted);
        SlotGame.winFieldController.showWinField(this._gameData!.totalWin);
        this._winCountUp.abort(this._gameData!.singleWin, isAborted);
        this._aborted = true;

        //if this is the last freespin round
        if((<EdisonParsedGameData>this._gameData)!.mode === EdisonGameMode.FREESPIN && (<EdisonParsedGameData>this._gameData)!.nextMode !== EdisonGameMode.FREESPIN) {
            EdisonGameModuleConfig.freespinController.deactivateFreespinOverlay();
        }
    }
}